import { castError } from '@sketch/utils'
import { DragEvent, useState } from 'react'
import { useApolloClient } from 'react-apollo'
import { DataProxy } from 'apollo-cache'

import { readShareListItemFromCache } from '../shares/utils'
import { readProjectFromCache } from '../projects'
import { ErrorHandler } from '@sketch/tracing'

import { ShareListItemFragment, ProjectFragment } from '@sketch/gql-types'

export interface ShareDropPayload {
  shareId: string
  share: ShareListItemFragment | undefined
}

export interface ProjectDropPayload {
  projectId: string
  project: ProjectFragment | undefined
}

export interface UseDropProps {
  onShareDropped?: (data: ShareDropPayload) => void
  onProjectDropped?: (data: ProjectDropPayload) => void
}

/**
 * Hook that exposes attributes to create a droppable area, accepts dragging
 * both shares or projects
 */
export const useDrop = ({ onShareDropped, onProjectDropped }: UseDropProps) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const [isDraggedOverItself, setDraggedOverItself] = useState(false)
  const cache = useApolloClient()

  if (!onShareDropped && !onProjectDropped) {
    return {
      kind: 'disabled',
    } as const
  }

  const resetDraggedOver = () => {
    setIsDraggedOver(false)
    setDraggedOverItself(false)
  }

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    if (event.dataTransfer.types[0] === 'text/plain') {
      /**
       * The code below runs only until setIsDraggedOver(true) to avoid performance issues during drag.
       * The data-being-dragged attribute, set by useShareDrop, helps track the element; check the hook for details.
       */
      if (!isDraggedOver) {
        if (event.target instanceof HTMLElement) {
          const draggedElement = document.querySelector(
            '[data-being-dragged="true"]'
          )
          const isItself = Boolean(draggedElement?.contains(event.target))

          setDraggedOverItself(isItself)
        }

        setIsDraggedOver(true) // This is to prevent the drag over event from being called multiple times
      }
    }
  }

  const onDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    resetDraggedOver()
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    // Avoid dropping on itself
    if (isDraggedOverItself) return

    resetDraggedOver()

    const payload = getEventData(cache, event)

    if (!payload) {
      return
    }

    if (payload && 'shareId' in payload) {
      onShareDropped?.(payload)
    } else if (payload && 'projectId' in payload) {
      onProjectDropped?.(payload)
    }
  }

  return {
    kind: 'enabled',
    isDraggedOver,
    isDraggedOverItself,
    onDrop,
    onDragOver,
    onDragLeave,
  } as const
}

const getEventData = (
  cache: DataProxy,
  event: DragEvent<HTMLElement>
): ShareDropPayload | ProjectDropPayload | undefined => {
  const dataType = event.dataTransfer.types[0]

  let dataObject: ShareDropPayload | ProjectDropPayload | undefined

  try {
    dataObject = JSON.parse(event.dataTransfer.getData(dataType))
  } catch (e) {
    const error = castError(e)
    ErrorHandler.ignore(error)
  }

  if (dataObject && 'shareId' in dataObject) {
    const { shareId } = dataObject

    if (!shareId) {
      return
    }

    const share = readShareListItemFromCache({ cache, id: shareId })

    return { shareId, share }
  }

  if (dataObject && 'projectId' in dataObject) {
    const { projectId } = dataObject

    if (!projectId) {
      return
    }

    const project = readProjectFromCache(cache, projectId)

    return { projectId, project }
  }

  return undefined
}
